import { FC } from 'react';
import { Paper, Typography, Box, Container, Stack } from '@mui/material';
import { usePhrase } from '@/hooks';
import { INTRO_PHRASES } from 'consts/phrases/introPhrases';

import { getThemeProps } from '@/components/layoutUtil';

import { IIntroIconData, IRegistrationTemplateResponse } from '@/types';

import { Icon } from '@/components/general/Icon/Icon';

import styles from '@/styles/modules/IntroIcons.module.css';

interface IntroIconsProps {
    registrationTemplate?: IRegistrationTemplateResponse;
    introIconsData: IIntroIconData[];
}

export const IntroIcons: FC<IntroIconsProps> = ({ registrationTemplate, introIconsData }) => {
    const { t } = usePhrase();

    const iconClassNames = `${styles.introIcon} introIcon`;

    const IntroPhrasesData = [
        {
            icon: <Icon path="introIcons/IntroIcon1" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_1_TITLE,
            text: INTRO_PHRASES.BOX_1_TEXT,
        },
        {
            icon: <Icon path="introIcons/IntroIcon2" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_2_TITLE,
            text: INTRO_PHRASES.BOX_2_TEXT,
        },
        {
            icon: <Icon path="introIcons/IntroIcon3" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_3_TITLE,
            text: INTRO_PHRASES.BOX_3_TEXT,
        },
        {
            icon: <Icon path="introIcons/IntroIcon4" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_4_TITLE,
            text: INTRO_PHRASES.BOX_4_TEXT,
        },
        {
            icon: <Icon path="introIcons/IntroIcon5" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_5_TITLE,
            text: INTRO_PHRASES.BOX_5_TEXT,
        },
        {
            icon: <Icon path="introIcons/IntroIcon6" className={iconClassNames} />,
            title: INTRO_PHRASES.BOX_6_TITLE,
            text: INTRO_PHRASES.BOX_6_TEXT,
        },
    ];

    const {
        homeLayout: {
            iconsBlock: { visible, position, inlineIcons, paperBox, iconQuantity },
        },
    } = getThemeProps();

    const bulletPoints = registrationTemplate?.template?.bulletPoints;

    const bulletsToShow = introIconsData.length ? introIconsData : IntroPhrasesData;

    const itemsLength = (bulletPoints?.length || iconQuantity) ?? undefined;

    const isLeftSide = position === 'left';
    const isBottom = position === 'bottom';

    if (!visible && !bulletPoints?.length) return null;

    return (
        <Box
            className={`introIcons ${styles.intro} ${paperBox ? `${styles.introLeftIcons}` : ''}`}
            data-testid="intro-box"
            sx={{ order: isLeftSide ? -1 : 1, mb: isBottom ? 4 : 0 }}
        >
            <Container
                className={`${styles.introContainer} ${inlineIcons ? styles.inlineIcons : ''} ${
                    isLeftSide ? `introLeftSide ${styles.leftSide}` : ''
                }`}
                data-testid="intro-container"
            >
                <Paper
                    className={`infoBox ${styles.introPaper}`}
                    sx={
                        paperBox
                            ? { p: 3 }
                            : { background: 'transparent', borderRadius: 0, boxShadow: 'none' }
                    }
                >
                    <Stack
                        direction={isLeftSide ? 'column' : 'row'}
                        className={`${styles.introStack} introStack`}
                        sx={{
                            flexWrap: 'wrap',
                        }}
                    >
                        {bulletsToShow.map((item, index) => {
                            if (itemsLength && index > itemsLength - 1) return;
                            return (
                                <Box
                                    key={item.title}
                                    className={`homeIntroIconsItem ${styles.introItem}`}
                                    data-testid="intro-item"
                                    sx={{
                                        width: {
                                            xs: '100%',
                                            sm: 'calc(50% - 30px)',
                                            lg: 'calc(33% - 30px)',
                                        },
                                    }}
                                >
                                    {item.icon}
                                    <Box className={`${styles.introContent} introIconContent`}>
                                        <Typography
                                            variant={inlineIcons ? 'h5' : 'h4'}
                                            className={`${styles.introTitle} introTitle`}
                                        >
                                            {t(item.title)}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            className={`${styles.introDescription} introDescription`}
                                        >
                                            {t(item.text)}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};
